import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Joyride from 'react-joyride';
import type { CallBackProps, Step } from 'react-joyride';
import { OnboardingContext, useOnboardingState } from './useOnboarding';
import { defaultWalkthroughSteps } from './tour-steps';
import { apiRequest } from '../../lib/api-request';
import { getUserId } from '../../lib/auth-session';
import { getApiUrl } from '../../lib/api-config';

async function markWalkthroughCompleted(userId: string): Promise<void> {
  try {
    await fetch(getApiUrl(`api/entities/user/${userId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        has_completed_joyride_walkthrough: true,
      })
    });
  } catch (_) {
    // Non-fatal: ignore write failure
  }
}

type OnboardingProviderProps = {
  children: React.ReactNode;
  autoStart?: boolean;
  steps?: Step[];
};

export function OnboardingProvider({ children, autoStart = true, steps }: OnboardingProviderProps) {
  const state = useOnboardingState(steps ?? defaultWalkthroughSteps);
  const { isRunning, stepIndex, steps: currentSteps, start, stop, setStepIndex, setSteps } = state;
  const joyrideRef = useRef<any>(null);
  const ignoreStepAfterRef = useRef<number | null>(null);

  useEffect(() => {
    if (steps && steps.length) setSteps(steps);
  }, [steps, setSteps]);

  // Cleanup overlay on mount and unmount - ensure it's hidden initially
  useEffect(() => {
    // Clean up any leftover overlay state on mount
    document.body.classList.remove('walkthrough-active');
    document.body.style.removeProperty('--walkthrough-clip');
    document.body.style.removeProperty('--walkthrough-glow-left');
    document.body.style.removeProperty('--walkthrough-glow-top');
    document.body.style.removeProperty('--walkthrough-glow-width');
    document.body.style.removeProperty('--walkthrough-glow-height');
    
    return () => {
      // Clean up on unmount
      document.body.classList.remove('walkthrough-active');
      document.body.style.removeProperty('--walkthrough-clip');
      document.body.style.removeProperty('--walkthrough-glow-left');
      document.body.style.removeProperty('--walkthrough-glow-top');
      document.body.style.removeProperty('--walkthrough-glow-width');
      document.body.style.removeProperty('--walkthrough-glow-height');
    };
  }, []);

  // Global error handler to catch Popper errors when targets become null
  useEffect(() => {
    if (!isRunning) return;
    
    const handleError = (event: ErrorEvent) => {
      // Check if this is a Popper/Joyride error related to null nodeName
      if (event.error && event.error.message && 
          (event.error.message.includes('nodeName') || 
           event.error.message.includes('Cannot read properties of null'))) {
        console.warn('Tour Popper error detected, stopping tour:', event.error);
        event.preventDefault(); // Prevent error from showing in console
        stop();
        // Mark tour as completed to prevent it from restarting
        (async () => {
          try {
            const userId = getUserId();
            if (userId) {
              await markWalkthroughCompleted(userId);
            }
          } catch (_) {
            // Non-fatal: ignore write failure
          }
        })();
      }
    };

    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, [isRunning, stop]);

  useEffect(() => {
    let isMounted = true;
    async function maybeStart() {
      try {
        if (!autoStart) return;
        const userId = getUserId();
        if (!userId) {
          start();
          return;
        }
        const userRecord = await apiRequest<{ has_completed_joyride_walkthrough?: boolean }>('api/account/me');
        if (!isMounted) return;
        if (!userRecord?.has_completed_joyride_walkthrough) start();
      } catch (_) {
        // Fail-open: still start the tour for new users
        start();
      }
    }
    maybeStart();
    return () => {
      isMounted = false;
    };
  }, [autoStart, start]);

  const handleJoyrideCallback = useCallback(async (data: CallBackProps) => {
    const { action, index, status, type } = data;

    // Handle skip immediately - stop tour and mark as completed
    if (action === 'skip' || status === 'skipped') {
      stop();
      try {
        const userId = getUserId();
        if (userId) {
          await markWalkthroughCompleted(userId);
        }
      } catch (_) {
        // Non-fatal: ignore write failure
      }
      return;
    }

    if (['tooltip'].includes(type) && action === 'close') {
      stop();
      return;
    }

    // Handle errors gracefully - if target is null or error occurs, stop the tour
    if (type === 'error' || status === 'error') {
      console.warn('Tour error detected, stopping tour:', data);
      stop();
      try {
        const userId = getUserId();
        if (userId) {
          await markWalkthroughCompleted(userId);
        }
      } catch (_) {
        // Non-fatal: ignore write failure
      }
      return;
    }

    if (type === 'step:after') {
      if (ignoreStepAfterRef.current === index) {
        ignoreStepAfterRef.current = null;
        return;
      }
      const current = currentSteps[index] as any | undefined;
      const currentId = current?.id as string | undefined;
      // Special case: leaving create-project -> wait for sidebar to be interactable
      if (currentId === 'create-project' && action !== 'prev') {
        const waitFor = '#nav-project-management';
        const ok = await (async () => {
          const startAt = Date.now();
          const timeoutMs = 2000;
          const intervalMs = 100;
          return await new Promise<boolean>((resolve) => {
            const timer = setInterval(() => {
              if (document.querySelector(waitFor)) {
                clearInterval(timer);
                resolve(true);
              } else if (Date.now() - startAt >= timeoutMs) {
                clearInterval(timer);
                resolve(false);
              }
            }, intervalMs);
          });
        })();
        setStepIndex(index + 1);
        if (ok && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
          try { joyrideRef.current.update(true); } catch (_) {}
        }
      } else {
        setStepIndex(index + (action === 'prev' ? -1 : 1));
      }
    }

    // If a target isn't found (e.g., route/UI just changed), wait briefly for next step's target
    if (type === 'target:notFound') {
      const nextIndex = index + 1;
      const next = currentSteps[nextIndex] as any | undefined;
      const nextSelector = typeof next?.target === 'string' ? (next.target as string) : undefined;

      const waitForSelector = (selector: string, timeoutMs = 3500, intervalMs = 100): Promise<boolean> => {
        return new Promise((resolve) => {
          const startAt = Date.now();
          const timer = setInterval(() => {
            if (document.querySelector(selector)) {
              clearInterval(timer);
              resolve(true);
            } else if (Date.now() - startAt >= timeoutMs) {
              clearInterval(timer);
              resolve(false);
            }
          }, intervalMs);
        });
      };

      if (nextSelector) {
        const found = await waitForSelector(nextSelector);
        setStepIndex(nextIndex);
        if (found && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
          try { joyrideRef.current.update(true); } catch (_) {}
        }
      } else {
        setStepIndex(nextIndex);
      }
    }

    if (['finished', 'skipped'].includes(status)) {
      stop();
      try {
        const userId = getUserId();
        if (userId) {
          await markWalkthroughCompleted(userId);
        }
      } catch (_) {
        // Non-fatal: ignore write failure
      }
    }
  }, [setStepIndex, stop, currentSteps]);

  // Auto-advance when users click certain spotlighted targets that reveal next UI
  useEffect(() => {
    if (!isRunning) return;
    const stepsById: Record<string, number> = {};
    currentSteps.forEach((s, i) => {
      const id = (s as any).id as string | undefined;
      if (id) stepsById[id] = i;
    });

    const currentStep = currentSteps[stepIndex] as any;
    const currentId = currentStep?.id as string | undefined;

    let removeListener: (() => void) | undefined;
    let removeSubmitListener: (() => void) | undefined;
    let removeObserver: (() => void) | undefined;
    let hasAdvanced = false;

    const waitForSelector = (selector: string, timeoutMs = 4000, intervalMs = 100): Promise<boolean> => {
      return new Promise((resolve) => {
        const startAt = Date.now();
        const timer = setInterval(() => {
          if (document.querySelector(selector)) {
            clearInterval(timer);
            resolve(true);
          } else if (Date.now() - startAt >= timeoutMs) {
            clearInterval(timer);
            resolve(false);
          }
        }, intervalMs);
      });
    };

    const advanceToNext = async (advanceDelayMs = 0, waitForNextSelector?: string) => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      const delay = Math.max(advanceDelayMs, 0);
      setTimeout(async () => {
        if (waitForNextSelector) {
          const found = await waitForSelector(waitForNextSelector);
          setStepIndex(stepIndex + 1);
          if (found && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
            try { joyrideRef.current.update(true); } catch (_) {}
          }
        } else {
          setStepIndex(stepIndex + 1);
        }
      }, delay);
    };

    const attachAdvanceOnClick = (selector: string, advanceDelayMs = 0, waitForNextSelector?: string) => {
      const el = document.querySelector(selector);
      if (!el) return;
      const handler = () => {
        advanceToNext(advanceDelayMs, waitForNextSelector);
      };
      el.addEventListener('click', handler, { once: true });
      removeListener = () => el.removeEventListener('click', handler as any);
    };

    const attachAdvanceOnSubmit = (formSelector: string, advanceDelayMs = 0, waitForNextSelector?: string) => {
      const form = document.querySelector(formSelector) as HTMLFormElement | null;
      if (!form) return;
      const handler = (e: Event) => {
        // Allow submission to proceed; just queue the advance
        advanceToNext(advanceDelayMs, waitForNextSelector);
      };
      form.addEventListener('submit', handler, { once: true });
      removeSubmitListener = () => form.removeEventListener('submit', handler as any);
    };

    const observeDialogCloseToAdvance = (dialogSelector: string, advanceDelayMs = 0, waitForNextSelector?: string) => {
      const targetNode = document.body;
      const observer = new MutationObserver(() => {
        const dialog = document.querySelector(dialogSelector);
        if (!dialog) {
          advanceToNext(advanceDelayMs, waitForNextSelector);
          observer.disconnect();
        }
      });
      observer.observe(targetNode, { childList: true, subtree: true });
      removeObserver = () => observer.disconnect();
    };

    // From welcome -> user will click Start (Next button), no custom handler
    if (currentId === 'start-project') {
      attachAdvanceOnClick('#new-project-btn', 0, '#new-project-dialog');
    }
    if (currentId === 'create-project') {
      const btn = document.querySelector('#create-project-btn');
      if (btn) {
        const handler = async () => {
          const nextIdx = stepIndex + 1;
          ignoreStepAfterRef.current = stepIndex;
          setStepIndex(nextIdx);
          await new Promise(r => setTimeout(r, 200));
          const ok = await (async () => {
            const start = Date.now();
            while (Date.now() - start < 5000) {
              if (document.querySelector('#nav-project-management')) return true;
              await new Promise(r => setTimeout(r, 100));
            }
            return false;
          })();
          if (ok && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
            try { joyrideRef.current.update(true); } catch (_) {}
          }
        };
        btn.addEventListener('click', handler, { once: true });
        const prev = removeListener;
        removeListener = () => {
          btn.removeEventListener('click', handler as any);
          if (prev) prev();
        };
      }

      const form = document.querySelector('#new-project-dialog form') as HTMLFormElement | null;
      if (form) {
        const submitHandler = async () => {
          const nextIdx = stepIndex + 1;
          ignoreStepAfterRef.current = stepIndex;
          setStepIndex(nextIdx);
          await new Promise(r => setTimeout(r, 200));
          const ok = await waitForSelector('#nav-project-management');
          if (ok && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
            try { joyrideRef.current.update(true); } catch (_) {}
          }
        };
        form.addEventListener('submit', submitHandler, { once: true });
        const prevSubmit = removeSubmitListener;
        removeSubmitListener = () => {
          form.removeEventListener('submit', submitHandler as any);
          if (prevSubmit) prevSubmit();
        };
      }

      const observer = new MutationObserver(() => {
        const dialog = document.querySelector('#new-project-dialog');
        if (!dialog) {
          advanceToNext(150, '#nav-project-management');
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const prevObs = removeObserver;
      removeObserver = () => {
        observer.disconnect();
        if (prevObs) prevObs();
      };
    }
    if (currentId === 'open-project-management') {
      attachAdvanceOnClick('#nav-project-management', 150, '#nav-todos');
    }
    if (currentId === 'open-todos') {
      attachAdvanceOnClick('#nav-todos', 250, '#new-todo-btn');
    }
    if (currentId === 'goto-todos') {
      attachAdvanceOnClick('#new-todo-btn', 0, '#new-todo-dialog');
    }
    if (currentId === 'todo-form') {
      // Advance when clicking the submit button
      const submitBtn = document.querySelector('#new-todo-dialog button[type="submit"]');
      if (submitBtn) {
        const handler = async () => {
          const nextIdx = stepIndex + 1;
          ignoreStepAfterRef.current = stepIndex;
          setStepIndex(nextIdx);
          await new Promise(r => setTimeout(r, 150));
          const ok = await waitForSelector('#new-todo-btn');
          if (ok && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
            try { joyrideRef.current.update(true); } catch (_) {}
          }
        };
        submitBtn.addEventListener('click', handler, { once: true });
        const prev = removeListener;
        removeListener = () => {
          submitBtn.removeEventListener('click', handler as any);
          if (prev) prev();
        };
      }
      // Advance on form submit (Enter key)
      const todoForm = document.querySelector('#new-todo-dialog form') as HTMLFormElement | null;
      if (todoForm) {
        const submitHandler = async () => {
          const nextIdx = stepIndex + 1;
          ignoreStepAfterRef.current = stepIndex;
          setStepIndex(nextIdx);
          await new Promise(r => setTimeout(r, 150));
          const ok = await waitForSelector('#new-todo-btn');
          if (ok && joyrideRef.current && typeof joyrideRef.current.update === 'function') {
            try { joyrideRef.current.update(true); } catch (_) {}
          }
        };
        todoForm.addEventListener('submit', submitHandler, { once: true });
        const prevSubmit = removeSubmitListener;
        removeSubmitListener = () => {
          todoForm.removeEventListener('submit', submitHandler as any);
          if (prevSubmit) prevSubmit();
        };
      }
      // Fallback: when dialog closes, advance and re-sync
      const observer = new MutationObserver(() => {
        const dialog = document.querySelector('#new-todo-dialog');
        if (!dialog) {
          advanceToNext(100, '#new-todo-btn');
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const prevObs = removeObserver;
      removeObserver = () => {
        observer.disconnect();
        if (prevObs) prevObs();
      };
    }

    return () => {
      if (removeListener) removeListener();
      if (removeSubmitListener) removeSubmitListener();
      if (removeObserver) removeObserver();
    };
  }, [isRunning, stepIndex, currentSteps, setStepIndex]);

  // Prevent dialogs from closing when interacting with Joyride UI during form-focused steps
  useEffect(() => {
    if (!isRunning) return;
    const currentStep = currentSteps[stepIndex] as any;
    const currentId = currentStep?.id as string | undefined;
    const shouldProtect = currentId === 'create-project' || currentId === 'todo-form';
    if (!shouldProtect) return;

    const stopIfJoyrideUi = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const isInJoyrideTooltip = !!target.closest('.react-joyride__tooltip');
      const isInJoyrideOverlay = !!target.closest('.react-joyride__overlay');
      if (isInJoyrideTooltip || isInJoyrideOverlay) {
        e.stopPropagation();
      }
    };

    document.addEventListener('pointerdown', stopIfJoyrideUi, true);
    return () => {
      document.removeEventListener('pointerdown', stopIfJoyrideUi, true);
    };
  }, [isRunning, stepIndex, currentSteps]);

  // LIGHTWEIGHT clip-path spotlight with MutationObserver for tooltip
  useEffect(() => {
    if (!isRunning) {
      document.body.style.removeProperty('--walkthrough-clip');
      document.body.style.removeProperty('--walkthrough-glow-left');
      document.body.style.removeProperty('--walkthrough-glow-top');
      document.body.style.removeProperty('--walkthrough-glow-width');
      document.body.style.removeProperty('--walkthrough-glow-height');
      document.body.classList.remove('walkthrough-active');
      return;
    }

    // Mark overlay as visible when tour is running
    document.body.classList.add('walkthrough-active');

    const step = currentSteps[stepIndex];
    const targetSelector = step?.target;
    const target = targetSelector && typeof targetSelector === 'string' && targetSelector !== 'body' 
      ? document.querySelector(targetSelector) 
      : null;
    const isBodyTarget = step.target === 'body';

    const applyMask = () => {
      const tooltip = document.querySelector('.react-joyride__tooltip') as HTMLElement;
      
      let clipPath = `M 0 0 H ${window.innerWidth} V ${window.innerHeight} H 0 Z `;
      let targetLeft = 0, targetTop = 0, targetWidth = 0, targetHeight = 0;
      
      // Add target cutout (button/form) - skip if body target or target is null
      if (!isBodyTarget && target && target.nodeName) {
        try {
          const rect = target.getBoundingClientRect();
        
        // Check if target is a dialog/form (has 'dialog' in the ID or is a form element)
        const isDialog = target.id.includes('dialog') || target.tagName === 'FORM' || target.closest('[role="dialog"]');
        const pad = isDialog ? 0 : 12;
        const radius = 10;

        targetLeft = Math.max(0, rect.left - pad);
        targetTop = Math.max(0, rect.top - pad);
        targetWidth = rect.width + pad * 2;
        targetHeight = rect.height + pad * 2;

        clipPath += `M ${targetLeft + radius} ${targetTop} `;
        clipPath += `L ${targetLeft + targetWidth - radius} ${targetTop} `;
        clipPath += `Q ${targetLeft + targetWidth} ${targetTop} ${targetLeft + targetWidth} ${targetTop + radius} `;
        clipPath += `L ${targetLeft + targetWidth} ${targetTop + targetHeight - radius} `;
        clipPath += `Q ${targetLeft + targetWidth} ${targetTop + targetHeight} ${targetLeft + targetWidth - radius} ${targetTop + targetHeight} `;
        clipPath += `L ${targetLeft + radius} ${targetTop + targetHeight} `;
        clipPath += `Q ${targetLeft} ${targetTop + targetHeight} ${targetLeft} ${targetTop + targetHeight - radius} `;
        clipPath += `L ${targetLeft} ${targetTop + radius} `;
        clipPath += `Q ${targetLeft} ${targetTop} ${targetLeft + radius} ${targetTop} Z `;
        } catch (e) {
          // Target element was removed or is invalid, skip target cutout
          console.warn('Target element invalid, skipping cutout:', e);
        }
      }

      // Add tooltip cutout if tooltip exists and is visible
      if (tooltip) {
        const tooltipRect = tooltip.getBoundingClientRect();
        
        // Only add cutout if tooltip has valid dimensions (is actually rendered)
        if (tooltipRect.width > 0 && tooltipRect.height > 0 && tooltipRect.top >= 0 && tooltipRect.top < window.innerHeight) {
          const tooltipPad = 0;
          const tooltipRadius = 12;
          
          const tooltipLeft = Math.max(0, tooltipRect.left - tooltipPad);
          const tooltipTop = Math.max(0, tooltipRect.top - tooltipPad);
          const tooltipWidth = tooltipRect.width + tooltipPad * 2;
          const tooltipHeight = tooltipRect.height + tooltipPad * 2;

          clipPath += `M ${tooltipLeft + tooltipRadius} ${tooltipTop} `;
          clipPath += `L ${tooltipLeft + tooltipWidth - tooltipRadius} ${tooltipTop} `;
          clipPath += `Q ${tooltipLeft + tooltipWidth} ${tooltipTop} ${tooltipLeft + tooltipWidth} ${tooltipTop + tooltipRadius} `;
          clipPath += `L ${tooltipLeft + tooltipWidth} ${tooltipTop + tooltipHeight - tooltipRadius} `;
          clipPath += `Q ${tooltipLeft + tooltipWidth} ${tooltipTop + tooltipHeight} ${tooltipLeft + tooltipWidth - tooltipRadius} ${tooltipTop + tooltipHeight} `;
          clipPath += `L ${tooltipLeft + tooltipRadius} ${tooltipTop + tooltipHeight} `;
          clipPath += `Q ${tooltipLeft} ${tooltipTop + tooltipHeight} ${tooltipLeft} ${tooltipTop + tooltipHeight - tooltipRadius} `;
          clipPath += `L ${tooltipLeft} ${tooltipTop + tooltipRadius} `;
          clipPath += `Q ${tooltipLeft} ${tooltipTop} ${tooltipLeft + tooltipRadius} ${tooltipTop} Z`;
        }
      }

      const clip = `path(evenodd, "${clipPath}")`;

      // Set CSS custom properties
      document.body.style.setProperty('--walkthrough-clip', clip);
      document.body.style.setProperty('--walkthrough-glow-left', `${targetLeft}px`);
      document.body.style.setProperty('--walkthrough-glow-top', `${targetTop}px`);
      document.body.style.setProperty('--walkthrough-glow-width', `${targetWidth}px`);
      document.body.style.setProperty('--walkthrough-glow-height', `${targetHeight}px`);

      console.log('✅ Applied clip-path mask');
    };

    // Apply immediately for target
    applyMask();

    let cleanupTooltipObs: (() => void) | undefined;
    let debounceTimer: NodeJS.Timeout | null = null;

    // Watch tooltip AND its size/position changes
    const watchTooltip = () => {
      const tooltip = document.querySelector('.react-joyride__tooltip') as HTMLElement;
      if (!tooltip) return;

      console.log('👁️ Starting to watch tooltip');

      // Observe tooltip for size/position changes
      const resizeObs = new ResizeObserver(() => {
        console.log('📏 Tooltip resized');
        applyMask();
      });
      resizeObs.observe(tooltip);

      // Re-apply when popper updates position
      const origSetAttribute = tooltip.setAttribute.bind(tooltip);
      tooltip.setAttribute = function(...args: [string, string]) {
        origSetAttribute(...args);
        if (args[0] === 'style' || args[0] === 'data-popper-placement') {
          console.log('📍 Tooltip repositioned');
          requestAnimationFrame(() => applyMask());
        }
      };

      return () => {
        resizeObs.disconnect();
        tooltip.setAttribute = origSetAttribute;
      };
    };

    // Debounced check for tooltip
    const checkForTooltip = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const tooltip = document.querySelector('.react-joyride__tooltip');
        if (tooltip && !cleanupTooltipObs) {
          console.log('✨ Tooltip appeared, applying mask');
          applyMask();
          cleanupTooltipObs = watchTooltip();
        }
      }, 50);
    };

    // Lightweight observer - only watch for .react-joyride__tooltip specifically
    const globalObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const hasJoyrideElement = addedNodes.some(node => 
            node instanceof HTMLElement && 
            (node.classList?.contains('react-joyride__tooltip') || 
             node.querySelector?.('.react-joyride__tooltip'))
          );
          
          if (hasJoyrideElement) {
            checkForTooltip();
            break;
          }
        }
      }
    });

    // Watch body with subtree but debounce the response
    globalObserver.observe(document.body, { childList: true, subtree: true });

    // Initial check for tooltip
    checkForTooltip();

    // Watch for layout shifts (like scrollbar appearing) - only for first step
    let layoutCheckInterval: NodeJS.Timeout | null = null;
    let positionCheckInterval: NodeJS.Timeout | null = null;
    if (stepIndex === 0 && isBodyTarget) {
      let lastWidth = window.innerWidth;
      layoutCheckInterval = setInterval(() => {
        if (window.innerWidth !== lastWidth) {
          console.log('📐 Layout shift detected, reapplying mask');
          lastWidth = window.innerWidth;
          applyMask();
        }
      }, 100);
      
      // Also watch tooltip position directly
      let lastTooltipLeft = -1;
      positionCheckInterval = setInterval(() => {
        const tooltip = document.querySelector('.react-joyride__tooltip');
        if (tooltip) {
          const rect = tooltip.getBoundingClientRect();
          if (lastTooltipLeft === -1) {
            lastTooltipLeft = rect.left;
          } else if (Math.abs(rect.left - lastTooltipLeft) > 5) {
            console.log('📍 Tooltip position shifted, reapplying mask');
            lastTooltipLeft = rect.left;
            applyMask();
          }
        }
      }, 100);
      
      // Stop checking after 3 seconds (page should be loaded by then)
      setTimeout(() => {
        if (layoutCheckInterval) clearInterval(layoutCheckInterval);
        if (positionCheckInterval) clearInterval(positionCheckInterval);
      }, 3000);
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (layoutCheckInterval) clearInterval(layoutCheckInterval);
      if (positionCheckInterval) clearInterval(positionCheckInterval);
      globalObserver.disconnect();
      if (cleanupTooltipObs) cleanupTooltipObs();
    };
  }, [isRunning, stepIndex, currentSteps]);

  // Validate current step target exists - if not, advance or stop
  useEffect(() => {
    if (!isRunning) return;
    const currentStep = currentSteps[stepIndex];
    if (!currentStep) return;
    
    const target = currentStep.target;
    if (target && typeof target === 'string' && target !== 'body') {
      const targetEl = document.querySelector(target);
      if (!targetEl) {
        console.warn(`Tour step target not found: ${target}, advancing to next step`);
        // If we're not on the last step, advance; otherwise stop
        if (stepIndex < currentSteps.length - 1) {
          setStepIndex(stepIndex + 1);
        } else {
          stop();
        }
      }
    }
  }, [isRunning, stepIndex, currentSteps, setStepIndex, stop]);

  // Provide a single place to control default spotlight sizing
  const DEFAULT_SPOTLIGHT_PADDING = 16;
  const stepsForJoyride = useMemo(() => {
    return currentSteps.map((s: any) => {
      return { spotlightPadding: DEFAULT_SPOTLIGHT_PADDING, ...s };
    });
  }, [currentSteps]);

  return (
    <OnboardingContext.Provider value={state}>
      {children}
      <Joyride
        ref={joyrideRef}
        steps={stepsForJoyride}
        run={isRunning}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        disableOverlay
        disableOverlayClicks
        disableScrolling
        spotlightClicks
        scrollToFirstStep
        styles={{
            options: {
              // Matches --z-toast (99) exactly, but react-joyride's own type
              // (node_modules/react-joyride/dist/index.d.ts) declares
              // options.zIndex as `number`, so it can't take a `var(...)`
              // string here without breaking tsc --noEmit.
              zIndex: 99,
              primaryColor: 'rgb(var(--gray-900))', // #111827 == --gray-900
              textColor: 'rgb(var(--gray-900))', // #111827 == --gray-900
              overlayColor: 'rgb(var(--black) / 0.6)', // dark overlay - clip-path creates holes
              spotlightBorderRadius: 12, // unmapped: no design-system radius token matches 12px (--radius is 0.5rem/8px); reused at spotlight/tooltip borderRadius and buttonNext's 10 below
            },
            spotlight: {
              borderRadius: 12, // unmapped design value, see options.spotlightBorderRadius comment above
              backgroundColor: 'transparent', // transparent - holes created by clip-path
              mixBlendMode: 'normal',
              boxShadow: 'none',
            },
            tooltip: {
              borderRadius: 12, // unmapped design value, see options.spotlightBorderRadius comment above
              boxShadow: '0 12px 24px rgba(0,0,0,0.18)', // unmapped: doesn't match --shadow-form or --shadow-subtle
              padding: '16px 16px 12px',
            },
            tooltipTitle: {
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 6,
            },
            tooltipContent: {
              fontSize: 14,
              lineHeight: 1.5,
            },
            buttonNext: {
              backgroundColor: 'rgb(var(--gray-900))', // #111827 == --gray-900
              borderRadius: 10, // unmapped: no design-system radius token matches 10px
            },
            buttonBack: {
              color: 'rgb(var(--gray-500))', // #6b7280 == --gray-500
            },
            buttonSkip: {
              color: 'rgb(var(--gray-500))', // #6b7280 == --gray-500
            },
          }}
          
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
        callback={handleJoyrideCallback}
      />
      <style>{`
        /* Lightweight CSS-only overlay with clip-path cutout */
        body::after {
          content: "";
          position: fixed;
          inset: 0;
          background: rgb(var(--black) / 0.7);
          pointer-events: none;
          z-index: 9998; /* unmapped: above --z-toast (99) by design (must cover the toast layer too); no token matches 9998 */
          clip-path: var(--walkthrough-clip, none);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s, visibility 0.2s;
        }
        
        body.walkthrough-active::after {
          opacity: 1;
          visibility: visible;
        }
        
        /* Glowing border around cutout */
        body::before {
          content: "";
          position: fixed;
          left: var(--walkthrough-glow-left, -9999px);
          top: var(--walkthrough-glow-top, -9999px);
          width: var(--walkthrough-glow-width, 0);
          height: var(--walkthrough-glow-height, 0);
          border-radius: 10px; /* unmapped: no design-system radius token matches 10px */
          pointer-events: none;
          z-index: 9999; /* unmapped: no token matches 9999 (must stack above the ::after overlay at 9998) */
          box-shadow: /* unmapped glow effect: rgba(255,255,255,*) doesn't match --shadow-form/--shadow-subtle; reused (with different alpha/spread) in the glowPulse keyframes below */
            0 0 0 3px rgba(255, 255, 255, 0.7),
            0 0 40px 6px rgba(255, 255, 255, 0.5);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s, visibility 0.2s;
        }
        
        body.walkthrough-active::before {
          opacity: 1;
          visibility: visible;
        }

        /* Ensure tooltip is above overlay and glow */
        .react-joyride__tooltip {
          z-index: 10000 !important; /* unmapped: no token matches 10000 (must stack above the glow border at 9999) */
          position: relative !important;
        }
        
        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 
              0 0 0 0px rgba(255, 255, 255, 0.6),
              0 0 30px 6px rgba(255, 255, 255, 0.4);
          }
          50% {
            box-shadow: 
              0 0 0 0px rgba(255, 255, 255, 0.8),
              0 0 40px 8px rgba(255, 255, 255, 0.6);
          }
        }
      `}</style>
    </OnboardingContext.Provider>
  );
}
